/**
 * Rentgrata Widget Block
 *
 * Injection block that loads the Rentgrata resident referral chat widget.
 * Per-property: each property has a unique Rentgrata property UUID.
 * Skips injection when no property_id is available.
 */

import { useEffect, useRef } from 'react';
import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken } from '@vibesites/runtime/react';

const SCRIPT_ID = 'rentgrata-widget-script';

function RentgrataWidgetComponent(_props: BlockProps) {
  const widgetKey = useBlockToken('property_id', {
    defaultField: 'custom_rentgrata_property_id',
  });
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!widgetKey) return;

    const existing = document.getElementById(SCRIPT_ID);
    if (existing) existing.remove();

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = 'https://widget.rentgrata.com/rentgrata.js';
    script.async = true;

    // The Rentgrata script creates globalThis.rentgrata.Widget on load
    // but does not auto-render — we must call Widget.render() with the
    // property's widget_key to initialize the chat widget.
    script.onload = () => {
      if (!mountedRef.current) return;
      const rg = (globalThis as any).rentgrata;
      if (rg?.Widget?.render) {
        rg.Widget.render({ widget_key: widgetKey, render_as: 'fixed' });
      }
    };

    document.head.appendChild(script);

    return () => {
      mountedRef.current = false;
      const el = document.getElementById(SCRIPT_ID);
      if (el) el.remove();
    };
  }, [widgetKey]);

  return null;
}

const contentSchema: ComponentContentSchema = {
  slot: 'RentgrataWidget',
  displayName: 'Rentgrata Widget',
  tokens: {
    property_id: {
      label: 'Property ID',
      type: 'text',
      defaultField: 'custom_rentgrata_property_id',
      suggestedFields: ['custom_rentgrata_property_id'],
      description: 'Rentgrata property UUID for the resident referral widget.',
      required: true,
    },
  },
};

export const rentgrataWidgetBlock: BlockDefinition = {
  id: 'rentgrata-widget',
  name: 'Rentgrata Widget',
  description: 'Resident referral chat widget powered by Rentgrata. Connects prospective renters with current residents.',
  category: 'Integrations',
  placement: 'injection',
  defaultScope: 'template',
  component: RentgrataWidgetComponent,
  contentSchema,
  tags: ['rentgrata', 'referral', 'chat', 'resident', 'widget'],
};
